'use client';

import { useMemo, useState } from 'react';
import { Search, Copy, Check, ChevronRight } from 'lucide-react';
import Alert24CTA from '@/components/Alert24CTA';
import {
  APPS,
  SEVERITY_ORDER,
  SEVERITY_STYLES,
  buildAgentConfig,
  patternPath,
  type AppLog,
  type LogPattern,
  type Severity,
} from './data';

function CopyButton({ text, label = 'Copy' }: { text: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        } catch {
          /* clipboard unavailable */
        }
      }}
      className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 transition-colors"
      aria-label={copied ? 'Copied' : label}
    >
      {copied ? <Check className="h-3.5 w-3.5 text-green-600" /> : <Copy className="h-3.5 w-3.5" />}
      {copied ? 'Copied' : label}
    </button>
  );
}

function SeverityBadge({ severity }: { severity: Severity }) {
  const s = SEVERITY_STYLES[severity];
  return (
    <span className={`inline-block rounded border px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${s.badge}`}>
      {s.label}
    </span>
  );
}

function PatternCard({ app, pattern }: { app: AppLog; pattern: LogPattern }) {
  const config = useMemo(() => buildAgentConfig(app, pattern), [app, pattern]);
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <h4 className="text-base font-semibold text-gray-900">{pattern.name}</h4>
        <SeverityBadge severity={pattern.severity} />
      </div>
      <p className="mb-3 text-sm text-gray-600">{pattern.catches}</p>

      <div className="mb-2">
        <div className="mb-1 flex items-center justify-between">
          <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">Regex pattern</span>
          <CopyButton text={pattern.regex} label="Copy regex" />
        </div>
        <pre className="overflow-x-auto rounded bg-gray-900 px-3 py-2 text-xs text-emerald-300">
          <code>{pattern.regex}</code>
        </pre>
      </div>

      <div className="mb-3">
        <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">Example matching line</span>
        <pre className="mt-1 overflow-x-auto rounded bg-gray-50 px-3 py-2 text-xs text-gray-700 border border-gray-200">
          <code>{pattern.exampleLine}</code>
        </pre>
      </div>

      <details className="group">
        <summary className="flex cursor-pointer list-none items-center gap-1 text-sm font-medium text-[#2813e8]">
          <ChevronRight className="h-4 w-4 transition-transform group-open:rotate-90" />
          Alert24 agent config
          <span className="ml-1 font-normal text-gray-400">({patternPath(app, pattern)})</span>
        </summary>
        <div className="mt-2">
          <div className="mb-1 flex justify-end">
            <CopyButton text={config} label="Copy config" />
          </div>
          <pre className="overflow-x-auto rounded bg-gray-900 px-3 py-2 text-xs text-gray-100">
            <code>{config}</code>
          </pre>
          <p className="mt-1 text-xs text-gray-500">
            Add this object to the <code className="rounded bg-gray-100 px-1">log_searches</code> array in your Alert24
            server-agent config. Adjust <code className="rounded bg-gray-100 px-1">path</code> for your distro if needed.
          </p>
        </div>
      </details>
    </div>
  );
}

function matchesQuery(app: AppLog, pattern: LogPattern, q: string): boolean {
  if (!q) return true;
  const hay = [
    app.name,
    pattern.name,
    pattern.catches,
    pattern.regex,
    pattern.exampleLine,
    pattern.id,
  ]
    .join(' ')
    .toLowerCase();
  return hay.includes(q);
}

export default function ToolClient() {
  const [query, setQuery] = useState('');
  const [activeApp, setActiveApp] = useState<string>('all');

  const q = query.trim().toLowerCase();

  const filteredApps = useMemo(() => {
    return APPS.map((app) => {
      const patterns = app.patterns.filter((p) => matchesQuery(app, p, q));
      return { app, patterns };
    }).filter(({ app, patterns }) => {
      if (activeApp !== 'all' && app.slug !== activeApp) return false;
      // When a keyword filters out all patterns, hide the app unless the
      // app name itself matches (then show all its patterns).
      if (q && patterns.length === 0 && !app.name.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [q, activeApp]);

  const totalPatterns = APPS.reduce((n, a) => n + a.patterns.length, 0);

  return (
    <div className="mx-auto max-w-5xl">
      {/* Controls */}
      <div className="mb-6 rounded-lg border border-gray-200 bg-gray-50 p-4">
        <div className="relative mb-3">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search patterns — e.g. deadlock, OOM, 5xx, failed password, timeout"
            className="w-full rounded-md border border-gray-300 bg-white py-2 pl-9 pr-3 text-sm focus:border-[#2813e8] focus:outline-none focus:ring-1 focus:ring-[#2813e8]"
          />
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setActiveApp('all')}
            className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
              activeApp === 'all' ? 'bg-[#2813e8] text-white' : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-100'
            }`}
          >
            All applications
          </button>
          {APPS.map((app) => (
            <button
              key={app.slug}
              type="button"
              onClick={() => setActiveApp(app.slug)}
              className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                activeApp === app.slug ? 'bg-[#2813e8] text-white' : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-100'
              }`}
            >
              {app.name}
            </button>
          ))}
        </div>
        <p className="mt-3 text-xs text-gray-500">
          {totalPatterns} curated patterns across {APPS.length} applications. Every regex is validated to match the example
          line shown. Generated configs use Alert24&rsquo;s <code className="rounded bg-gray-100 px-1">file</code> log-search
          schema.
        </p>
      </div>

      {/* App sections */}
      <div className="space-y-10">
        {filteredApps.length === 0 && (
          <p className="rounded-lg border border-dashed border-gray-300 bg-white p-8 text-center text-sm text-gray-500">
            No patterns match &ldquo;{query}&rdquo;. Try a broader term like &ldquo;error&rdquo;, &ldquo;timeout&rdquo;, or
            &ldquo;memory&rdquo;.
          </p>
        )}

        {filteredApps.map(({ app, patterns }) => {
          // If the app name matched but patterns were filtered to empty, show all patterns.
          const shown = patterns.length > 0 ? patterns : app.patterns;
          const ordered = [...shown].sort(
            (a, b) => SEVERITY_ORDER.indexOf(a.severity) - SEVERITY_ORDER.indexOf(b.severity)
          );
          return (
            <section key={app.slug} id={app.slug} className="scroll-mt-24">
              <div className="mb-1 flex flex-wrap items-baseline justify-between gap-2">
                <h3 className="text-xl font-bold text-gray-900">
                  <a href={`#${app.slug}`} className="hover:text-[#2813e8]">
                    {app.name}
                  </a>
                </h3>
                <span className="text-xs font-medium uppercase tracking-wide text-gray-400">{app.category}</span>
              </div>
              <p className="mb-3 text-sm text-gray-600">{app.tagline}</p>

              {/* Paths */}
              <div className="mb-4 rounded-lg border border-gray-200 bg-white p-3">
                <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">Default log locations</span>
                <table className="mt-2 w-full text-xs">
                  <thead>
                    <tr className="text-left text-gray-500">
                      <th className="pb-1 pr-3 font-medium">What</th>
                      <th className="pb-1 pr-3 font-medium">Debian / Ubuntu</th>
                      <th className="pb-1 font-medium">RHEL / CentOS</th>
                    </tr>
                  </thead>
                  <tbody className="font-mono text-gray-700">
                    {app.paths.map((p) => (
                      <tr key={p.label} className="border-t border-gray-100">
                        <td className="py-1 pr-3 font-sans text-gray-600">{p.label}</td>
                        <td className="py-1 pr-3">{p.debian}</td>
                        <td className="py-1">{p.rhel ?? '(same)'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="mt-3">
                  <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">Log format example</span>
                  {app.formatExamples.map((ex, i) => (
                    <pre key={i} className="mt-1 overflow-x-auto rounded bg-gray-50 px-3 py-2 text-xs text-gray-700 border border-gray-200">
                      <code>{ex}</code>
                    </pre>
                  ))}
                </div>
              </div>

              {/* Patterns */}
              <div className="grid gap-3 md:grid-cols-2">
                {ordered.map((pattern) => (
                  <PatternCard key={pattern.id} app={app} pattern={pattern} />
                ))}
              </div>
            </section>
          );
        })}
      </div>

      {/* CTA */}
      <div className="mt-12">
        <Alert24CTA variant="log-search" source="log-patterns" />
        <p className="mt-2 text-center text-xs text-gray-500">
          Alert24 includes 3 server agents free. Paid plans add 5 agents per unit; log-search monitoring is a paid feature.
        </p>
      </div>
    </div>
  );
}
